import React, { useState, useEffect } from 'react';
import { getCategorias, getEtiquetasPorCategoria } from '../config/categoriasConfig';
import ImageUploadField from './ImageUploadField';
import { motion } from 'framer-motion';

const DIFICULTAD_HEADER_GRADIENT = {
  facil: 'from-emerald-500 to-teal-500 dark:from-emerald-600 dark:to-teal-600',
  medio: 'from-amber-500 to-orange-500 dark:from-amber-600 dark:to-orange-600',
  dificil: 'from-rose-500 to-fuchsia-500 dark:from-rose-600 dark:to-fuchsia-600',
  default: 'from-slate-500 to-slate-600 dark:from-slate-600 dark:to-slate-700'
};

const getHeaderGradientByDifficulty = (dificultad) => {
  const value = (dificultad || '').toString().trim().toLowerCase();
  return DIFICULTAD_HEADER_GRADIENT[value] || DIFICULTAD_HEADER_GRADIENT.default;
};

const ModificarPreguntaModal = ({ pregunta, isOpen = true, onClose, onSave, onDelete }) => {
  const categoriasOrdenadas = getCategorias();
  const [formData, setFormData] = useState({
    pregunta: '',
    respuesta_correcta: '',
    respuesta_falsa1: '',
    respuesta_falsa2: '',
    respuesta_falsa3: '',
    categoria: '',
    etiqueta: '',
    dificultad: '',
    explicacion: '',
    imagen: '',
    imagen_temp_token: ''
  });
  const [etiquetasDisponibles, setEtiquetasDisponibles] = useState([]);
  const [cargaInicialCompletada, setCargaInicialCompletada] = useState(false); // Nuevo estado para controlar la carga inicial

  useEffect(() => {
    if (pregunta) {
      const categoriaPregunta = pregunta.categoria || '';
      const etiquetaPregunta = pregunta.etiqueta || '';
      
      setFormData(prev => ({
        pregunta: pregunta.pregunta || '',
        respuesta_correcta: pregunta.respuesta_correcta || '',
        respuesta_falsa1: pregunta.respuesta_falsa1 || '',
        respuesta_falsa2: pregunta.respuesta_falsa2 || '',
        respuesta_falsa3: pregunta.respuesta_falsa3 || '',
        categoria: categoriaPregunta,
        etiqueta: etiquetaPregunta,
        dificultad: pregunta.dificultad || '',
        explicacion: pregunta.explicacion || '',
        imagen: pregunta.imagen_url || '',
        imagen_temp_token: ''
      }));
      
      // Actualizar etiquetas disponibles cuando se carga la pregunta
      if (categoriaPregunta) {
        setEtiquetasDisponibles(getEtiquetasPorCategoria(categoriaPregunta) || []);
      } else {
        setEtiquetasDisponibles([]);
      }
      
      // Marcar que la carga inicial se ha completado
      setCargaInicialCompletada(true);
    }
  }, [pregunta]);

  useEffect(() => {
    // Actualizar etiquetas disponibles cuando cambia la categoría, pero solo después de la carga inicial
    if (cargaInicialCompletada && formData.categoria) {
      setEtiquetasDisponibles(getEtiquetasPorCategoria(formData.categoria) || []);
    } else if (cargaInicialCompletada && !formData.categoria) {
      setEtiquetasDisponibles([]);
    }
  }, [formData.categoria, cargaInicialCompletada]);

  useEffect(() => {
    if (!isOpen || !pregunta) {
      return undefined;
    }

    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      let updatedData = { ...prev, [name]: value };
      
      // Si se cambia la categoría, verificar si la etiqueta actual es válida para la nueva categoría
      // Pero solo hacerlo si NO es la carga inicial
      if (name === 'categoria' && cargaInicialCompletada) {
        const etiquetasDisponibles = getEtiquetasPorCategoria(value) || [];
        // Si la etiqueta actual no está en las disponibles para la nueva categoría, resetearla
        if (etiquetasDisponibles.length > 0 && !etiquetasDisponibles.includes(prev.etiqueta)) {
          updatedData.etiqueta = ''; // Resetear etiqueta si no es válida para la nueva categoría
        } else if (etiquetasDisponibles.length > 0 && etiquetasDisponibles.includes(prev.etiqueta)) {
          // Si la etiqueta actual sí es válida para la nueva categoría, mantenerla
          updatedData.etiqueta = prev.etiqueta;
        } else {
          // Si no hay etiquetas disponibles para la categoría, resetear la etiqueta
          updatedData.etiqueta = '';
        }
      }
      
      return updatedData;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validar campos requeridos
    if (!formData.pregunta.trim()) {
      console.error('La pregunta es obligatoria.');
      return;
    }
    if (!formData.respuesta_correcta.trim()) {
      console.error('La respuesta correcta es obligatoria.');
      return;
    }
    if (!formData.categoria) {
      console.error('La categoría es obligatoria.');
      return;
    }
    if (!formData.etiqueta) {
      console.error('La etiqueta es obligatoria.');
      return;
    }
    if (!formData.dificultad) {
      console.error('La dificultad es obligatoria.');
      return;
    }

    // Asegurarse de que el ID de la pregunta original se incluya en los datos actualizados
    onSave({
      ...formData,
      id: pregunta.id
    });
  };

  if (!isOpen || !pregunta) return null;

  const fieldLabelClass = 'block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5 sm:mb-2';
  const baseInputClass = 'w-full px-2.5 sm:px-3 py-2 sm:py-2.5 text-xs sm:text-sm border border-gray-300 rounded-md bg-white text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-500 dark:text-white transition-colors duration-200';
  const requiredMarkClass = 'text-rose-500 dark:text-rose-400';
  const headerGradientClass = getHeaderGradientByDifficulty(formData.dificultad || pregunta?.dificultad);

  return (
    <motion.div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      onClick={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
    >
      <motion.div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 18, scale: 0.985 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 18, scale: 0.985 }}
        transition={{ duration: 0.24, ease: 'easeOut' }}
      >
        <div className="p-4 sm:p-7">
          <div className={`sticky top-0 z-10 -mx-4 sm:-mx-7 -mt-4 sm:-mt-7 px-4 sm:px-7 pt-5 pb-4 mb-6 bg-gradient-to-r ${headerGradientClass} backdrop-blur border-b border-white/25`}>
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-xl font-semibold text-white">Modificar Pregunta</h3>
                <p className="text-sm text-white/85 mt-1">
                  Revisa el contenido antes de aprobar o rechazar.
                </p>
              </div>
            <button 
              onClick={onClose}
              className="text-white/85 hover:text-white text-2xl leading-none"
              aria-label="Cerrar modal"
            >
              &times;
            </button>
            </div>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="mb-5">
              <label className={fieldLabelClass}>
                Pregunta <span className={requiredMarkClass}>*</span>
              </label>
              <textarea
                name="pregunta"
                value={formData.pregunta}
                onChange={handleChange}
                required
                className={`${baseInputClass} min-h-[110px] resize-y`}
                rows="3"
                placeholder="Escribe la pregunta que veran los participantes"
              />
            </div>

            <div className="mb-5 p-4 rounded-xl border border-orange-100 dark:border-slate-600 bg-gradient-to-br from-orange-50/80 to-amber-50/70 dark:from-slate-900/30 dark:to-slate-800/40">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-3">
                Respuestas
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-4">
              <div>
                <label className={`${fieldLabelClass} !text-emerald-600 dark:!text-emerald-400`}>
                  Respuesta Correcta <span className={requiredMarkClass}>*</span>
                </label>
                <input
                  type="text"
                  name="respuesta_correcta"
                  value={formData.respuesta_correcta}
                  onChange={handleChange}
                  required
                  className={baseInputClass}
                />
              </div>

              <div>
                <label className={`${fieldLabelClass} !text-rose-600 dark:!text-rose-400`}>Respuesta Falsa 1</label>
                <input
                  type="text"
                  name="respuesta_falsa1"
                  value={formData.respuesta_falsa1}
                  onChange={handleChange}
                  className={baseInputClass}
                />
              </div>

              <div>
                <label className={`${fieldLabelClass} !text-rose-600 dark:!text-rose-400`}>Respuesta Falsa 2</label>
                <input
                  type="text"
                  name="respuesta_falsa2"
                  value={formData.respuesta_falsa2}
                  onChange={handleChange}
                  className={baseInputClass}
                />
              </div>

              <div>
                <label className={`${fieldLabelClass} !text-rose-600 dark:!text-rose-400`}>Respuesta Falsa 3</label>
                <input
                  type="text"
                  name="respuesta_falsa3"
                  value={formData.respuesta_falsa3}
                  onChange={handleChange}
                  className={baseInputClass}
                />
              </div>
            </div>
            </div>

            <div className="mb-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-4">
              <div>
                <label className={fieldLabelClass}>
                  Categoria <span className={requiredMarkClass}>*</span>
                </label>
                <select
                  name="categoria"
                  value={formData.categoria}
                  onChange={handleChange}
                  required
                  className={baseInputClass}
                >
                  <option value="" className="dark:bg-gray-600 dark:text-white">Seleccionar categoría</option>
                  {categoriasOrdenadas.map(categoria => (
                    <option key={categoria.id} value={categoria.id} className="dark:bg-gray-600 dark:text-white">
                      {categoria.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={fieldLabelClass}>
                  Etiqueta <span className={requiredMarkClass}>*</span>
                </label>
                <select
                  name="etiqueta"
                  value={formData.etiqueta}
                  onChange={handleChange}
                  required
                  disabled={!formData.categoria}
                  className={`${baseInputClass} disabled:opacity-60 disabled:cursor-not-allowed`}
                >
                  <option value="" className="dark:bg-gray-600 dark:text-white">Seleccionar etiqueta</option>
                  {etiquetasDisponibles.map(etiqueta => (
                    <option key={etiqueta} value={etiqueta} className="dark:bg-gray-600 dark:text-white">
                      {etiqueta}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-5">
              <label className={fieldLabelClass}>
                Dificultad <span className={requiredMarkClass}>*</span>
              </label>
              <select
                name="dificultad"
                value={formData.dificultad}
                onChange={handleChange}
                required
                className={baseInputClass}
              >
                <option value="" disabled className="dark:bg-gray-600 dark:text-white">Seleccionar dificultad</option>
                <option value="facil" className="dark:bg-gray-600 dark:text-white">Fácil</option>
                <option value="medio" className="dark:bg-gray-600 dark:text-white">Medio</option>
                <option value="dificil" className="dark:bg-gray-600 dark:text-white">Difícil</option>
              </select>
            </div>

            <div className="mb-6 grid grid-cols-1 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,0.9fr)] lg:grid-cols-[1.6fr_1fr] gap-3 sm:gap-4 items-start">
              <div className="order-2 sm:order-1">
                <label className={fieldLabelClass}>Explicacion (Opcional)</label>
                <textarea
                  name="explicacion"
                  value={formData.explicacion}
                  onChange={handleChange}
                  className={`${baseInputClass} min-h-[170px] sm:min-h-[190px] max-h-[320px] resize-y`}
                  rows="4"
                  placeholder="Anade una explicacion para facilitar la revision"
                />
              </div>
              <div className="order-1 sm:order-2">
                <ImageUploadField
                  value={formData.imagen}
                  tempToken={formData.imagen_temp_token}
                  previewMaxHeightClass="max-h-72 sm:max-h-80"
                  onChange={(imagen, imagenTempToken) => setFormData(prev => ({
                    ...prev,
                    imagen,
                    imagen_temp_token: imagenTempToken
                  }))}
                />
              </div>
            </div>

            <div className="pt-4 mt-1 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-center sm:justify-end gap-3">
              <button
                type="submit"
                className="px-5 py-2 rounded-md bg-orange-500 text-white font-medium hover:bg-orange-600 transition-colors duration-200 dark:bg-orange-600 dark:hover:bg-orange-700 inline-flex items-center gap-2"
              >
                <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" aria-hidden="true">
                  <path d="M5 12l4 4L19 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Modificar
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 transition-colors duration-200 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-500 dark:hover:bg-gray-600 inline-flex items-center gap-2"
              >
                <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" aria-hidden="true">
                  <path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                Cancelar
              </button>
              {typeof onDelete === 'function' && (
                <button
                  type="button"
                  onClick={() => onDelete(pregunta.id)}
                  className="h-10 w-10 inline-flex items-center justify-center rounded-md bg-rose-600 text-white hover:bg-rose-700 transition-colors duration-200"
                  aria-label="Eliminar pregunta"
                  title="Eliminar pregunta"
                >
                  <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
                    <path d="M4 7h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M7 7l1 12a1 1 0 0 0 1 .9h6a1 1 0 0 0 1-.9L17 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M10 11v5M14 11v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              )}
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ModificarPreguntaModal;
